import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { Crest } from '@/components/Logo'
import { IconButton } from '@/components/ui'
import { OfferPageCard } from './OfferPageCard'
import type { Offer } from '@shared/model'

/**
 * Häftet i helskärm, en sida i taget.
 *
 * Listvyn är till för att hitta något bestämt. Det här läget är till för det
 * andra: att bläddra igenom häftet utan att resten av appen syns, som man
 * gör med pappersversionen vid köksbordet. Därför försvinner sidhuvud,
 * sökfält och navigering helt medan det är öppet.
 *
 * Bläddringen bygger på webbläsarens egen scroll-snap i stället för ett
 * bibliotek – svepet blir mjukare på mobil än något vi kan skriva själva,
 * och piltangenter fungerar utan extra kod.
 */
export function FlipbookOverlay({
  offers,
  owned,
  remainingFor,
  periodName,
  onOpenOffer,
  onClose,
}: {
  offers: Offer[]
  owned: number
  remainingFor: (offer: Offer) => number
  periodName: string
  onOpenOffer: (offer: Offer) => void
  onClose: () => void
}) {
  const scroller = useRef<HTMLDivElement>(null)
  const [index, setIndex] = useState(0)

  const goTo = useCallback(
    (next: number) => {
      const element = scroller.current
      if (!element) return
      const clamped = Math.max(0, Math.min(offers.length - 1, next))
      element.scrollTo({ left: clamped * element.clientWidth, behavior: 'smooth' })
    },
    [offers.length],
  )

  // Esc stänger, pilarna bläddrar. Samma förväntan som i en bildvisare.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
      if (event.key === 'ArrowRight') goTo(index + 1)
      if (event.key === 'ArrowLeft') goTo(index - 1)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [index, goTo, onClose])

  // Sidan bakom ska inte gå att rulla medan häftet ligger över.
  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Bläddra i ${periodName}`}
      className="fixed inset-0 z-50 flex flex-col bg-bg pt-safe pb-safe motion-safe:animate-[fade-in_180ms_ease-out]"
    >
      <header className="flex items-center gap-3 border-b border-border bg-brand-surface px-3 py-2.5 text-on-brand">
        <Crest size={30} />
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-sm font-extrabold">{periodName}</p>
          <p className="text-xs text-accent">
            Sida {index + 1} av {offers.length}
          </p>
        </div>
        <IconButton
          label="Stäng häftet"
          variant="ghost"
          onClick={onClose}
          className="text-on-brand hover:bg-white/15"
        >
          <X size={24} aria-hidden />
        </IconButton>
      </header>

      <div
        ref={scroller}
        onScroll={(event) => {
          const element = event.currentTarget
          setIndex(Math.round(element.scrollLeft / Math.max(1, element.clientWidth)))
        }}
        className="flex min-h-0 flex-1 snap-x snap-mandatory overflow-x-auto overflow-y-hidden no-scrollbar"
      >
        {offers.map((offer, position) => (
          <div key={offer.id} className="h-full w-full shrink-0 snap-center p-3">
            <OfferPageCard
              brandColor={offer.companyBrandColor}
              offer={offer}
              remaining={remainingFor(offer)}
              owned={owned}
              pageNumber={position + 1}
              pageCount={offers.length}
              onOpen={() => onOpenOffer(offer)}
            />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-center gap-5 px-4 py-3">
        <IconButton
          label="Föregående sida"
          variant="quiet"
          disabled={index === 0}
          onClick={() => goTo(index - 1)}
        >
          <ChevronLeft size={24} aria-hidden />
        </IconButton>

        {/* Prickarna ger en känsla för hur tjockt häftet är. Blir de för
            många krymper de till en ren sifferräknare. */}
        {offers.length <= 12 ? (
          <div className="flex items-center gap-1.5" aria-hidden>
            {offers.map((offer, position) => (
              <span
                key={offer.id}
                className={
                  position === index
                    ? 'h-2 w-5 rounded-pill bg-brand-ink transition-all'
                    : 'h-2 w-2 rounded-pill bg-border-strong transition-all'
                }
              />
            ))}
          </div>
        ) : (
          <span className="font-display text-sm font-bold tabular-nums text-muted">
            {index + 1} / {offers.length}
          </span>
        )}

        <IconButton
          label="Nästa sida"
          variant="quiet"
          disabled={index >= offers.length - 1}
          onClick={() => goTo(index + 1)}
        >
          <ChevronRight size={24} aria-hidden />
        </IconButton>
      </div>
    </div>
  )
}
